/** ExcelJS renderer for the Privilege Report workbook spec.
 *
 *  All content and per-cell style decisions live in the spec
 *  (privilegeReportWorkbook.js — pure and unit-tested); this module only
 *  translates spec style tokens into ExcelJS font/fill/border objects. Layout
 *  contract (matches the approved preview): row 1 title, row 2 subtitle (both
 *  merged full-width), row 3 blank, row 4 header band, data from row 5 with a
 *  freeze pane under the header and an autofilter over header+data.
 */

import ExcelJS from "exceljs/dist/exceljs.min.js";
import { COLORS } from "@/utils/privilegeReportWorkbook";

const thin = { style: "thin", color: { argb: COLORS.border } };
const GRID_BORDER = { top: thin, left: thin, bottom: thin, right: thin };

const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

// Spec style token → ExcelJS partial style.
const TOKEN_STYLES = {
  riskHigh: { font: { bold: true, size: 10, color: { argb: COLORS.redText } }, fill: solid(COLORS.redFill), alignment: { horizontal: "center" } },
  riskMedium: { font: { size: 10, color: { argb: COLORS.yellowText } }, fill: solid(COLORS.yellowFill), alignment: { horizontal: "center" } },
  riskNone: { font: { size: 10, color: { argb: COLORS.greenText } }, fill: solid(COLORS.greenFill), alignment: { horizontal: "center" } },
  unscored: { font: { size: 9, italic: true, color: { argb: COLORS.gray } }, alignment: { horizontal: "center" } },
  statusOverride: { font: { size: 10 }, fill: solid(COLORS.overrideFill) },
  statusDenied: { font: { bold: true, size: 10, color: { argb: COLORS.redText } }, fill: solid(COLORS.redFill) },
  flagBeyond: { font: { bold: true, size: 10, color: { argb: COLORS.redText } }, fill: solid(COLORS.redFill), alignment: { horizontal: "center" } },
  centered: { font: { size: 10 }, alignment: { horizontal: "center" } },
};

function applyTitleRows(ws, title, subtitle, width) {
  const span = Math.max(width, 1);
  ws.mergeCells(1, 1, 1, span);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.navy } };
  ws.mergeCells(2, 1, 2, span);
  const subCell = ws.getCell(2, 1);
  subCell.value = subtitle;
  subCell.font = { size: 10, color: { argb: COLORS.gray } };
  subCell.alignment = { vertical: "top", wrapText: true };
  ws.getRow(2).height = 28;
}

function renderDataSheet(wb, sheet) {
  const ws = wb.addWorksheet(sheet.name);
  const span = Math.max(sheet.headers.length, 1);
  ws.columns = sheet.headers.map((_, i) => ({ width: sheet.widths[i] || 12 }));
  applyTitleRows(ws, sheet.title, sheet.subtitle, span);

  const headerRow = ws.getRow(4);
  sheet.headers.forEach((header, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = header;
    cell.font = { bold: true, size: 10, color: { argb: COLORS.headerText } };
    cell.fill = solid(COLORS.navy);
    cell.border = GRID_BORDER;
    cell.alignment = { vertical: "middle", wrapText: true };
  });

  sheet.rows.forEach((cells, r) => {
    const row = ws.getRow(5 + r);
    cells.forEach((spec, c) => {
      const cell = row.getCell(c + 1);
      cell.value = spec.value;
      cell.border = GRID_BORDER;
      const token = spec.style && TOKEN_STYLES[spec.style];
      if (token) {
        if (token.font) cell.font = token.font;
        if (token.fill) cell.fill = token.fill;
        if (token.alignment) cell.alignment = token.alignment;
      } else {
        cell.font = { size: 10 };
      }
    });
  });

  ws.views = [{ state: "frozen", ySplit: 4, topLeftCell: "A5", activePane: "bottomLeft" }];
  if (sheet.rows.length) {
    ws.autoFilter = {
      from: { row: 4, column: 1 },
      to: { row: 4 + sheet.rows.length, column: span },
    };
  }
  return ws;
}

/**
 * Render the spec to an ExcelJS Workbook, sheets in spec order.
 * @param {{sheets: Array<object>}} spec from buildWorkbookSpec()
 * @returns {ExcelJS.Workbook}
 */
export function renderWorkbook(spec) {
  const wb = new ExcelJS.Workbook();
  for (const sheet of spec.sheets) {
    renderDataSheet(wb, sheet);
  }
  return wb;
}
